<template>
  <div class="flex-1 flex items-center h-full">
    <div
      class="flex-1 flex items-center justify-center transition-all duration-300 relative overflow-visible h-full"
    >
      <div
        v-if="holdActive"
        class="absolute top--40 left-0 right-0 bg-white h-40 flex flex-nowrap whitespace-nowrap flex-col items-center justify-center mb-2 transition-all,0.3s transition-ease"
      >
        <span v-if="isCancelling" class="text-red-500">
          {{ t("message.ai.releaseToCancel") }}
        </span>
        <span v-else class="text-gray-500">
          {{ t("message.ai.swipeUpToCancel") }}
        </span>
      </div>
      <div
        class="record-button h-full size-full flex text-14 font-bold cursor-pointer transition-all duration-200 mx--10 touch-none"
        :class="{
          active: holdActive,
          cancelling: isCancelling,
        }"
        @touchstart="handleTouchStart"
        @touchmove="handleTouchMove"
        @touchend="handleTouchEnd"
      >
        <div
          class="pointer-events-none w-full flex justify-center items-center h-full"
        >
          <AudioLevelDisplay
            v-if="holdActive"
            :is-canceling="isCancelling"
            :audio-level="audioLevel / 100"
          />
          <span v-else>{{ t("message.ai.holdToTalk") }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from "vue";
import { useAudioRecorder } from "@/composables/use-audio-recorder";
import AudioLevelDisplay from "./AudioLevelDisplay.vue";
import { showToast } from "vant";
import { getAuth } from "@voghion/native-api";
import { checkApi } from "../utils/checkApi";
import { useI18n } from "vue-i18n";
import { useSetting } from "@/composables/use-setting";
import { showUpdateTips } from "../utils/update-tips";

const recorder = useAudioRecorder();

const { t } = useI18n();
const { getClientInfo } = useSetting();
const holdActive = defineModel<boolean>("holdActive");

const emits = defineEmits<{
  end: [audioBlob: Blob];
}>();

let touchTime = 0;
let timer: NodeJS.Timeout | null = null;

const audioLevel = computed(() => recorder?.audioLevel.value || 0);

const startY = ref(0);
const currentY = ref(0);
const isCancelling = ref(false);

const getRecordAuth = async () => {
  const res = await checkApi(getAuth);
  return res === undefined ? undefined : !!res?.data?.record;
};

let recordAuth: boolean | undefined | null = null;

const handleTouchStart = async (event: TouchEvent) => {
  touchTime = 0;
  if (!recorder) return;
  // recordAuth 为 null 时，表示未获取过录音权限
  if (recordAuth === null) {
    recordAuth = await getRecordAuth();
  }
  // recordAuth 为 undefined 时，表示版本不支持js 录音 api（ios)，打开更新提示
  if (recordAuth === undefined) {
    const clientInfo = getClientInfo();
    showUpdateTips(clientInfo.country, t);
    return;
  }
  // recordAuth 为 true 时，表示已获有录音权限
  if (recordAuth) {
    if (holdActive.value) {
      holdActive.value = false;
      return;
    }
    touchTime = Date.now();
    if (event.cancelable) {
      event.preventDefault();
    }
    startY.value = event.touches[0].clientY;
    currentY.value = startY.value;
    startHoldAction();
  } else {
    // recordAuth 为 false 时，表示拒绝了录音权限，重新打开询问弹框
    const res = await recorder.open();
    recordAuth = res;
  }
};

const handleTouchMove = (event: TouchEvent) => {
  if (event.cancelable) {
    event.preventDefault();
  }
  if (!holdActive.value) return;

  currentY.value = event.touches[0].clientY;
  const moveDistance = startY.value - currentY.value;

  // 上划超过35px时取消发送
  isCancelling.value = moveDistance > 35;
};

const handleTouchEnd = async (event: TouchEvent) => {
  const holdTime = Date.now() - touchTime;
  const tooShort = holdTime < 700;
  if (!touchTime || tooShort) {
    recorder?.close();
    holdActive.value = false;
    if (!touchTime) return;
    touchTime = 0;
    if (tooShort) {
      return showToast(t("message.ai.speakTimeTooShort"));
    }
  }

  if (event.cancelable) {
    event.preventDefault();
  }
  endHoldAction();
};

const startHoldAction = async () => {
  if (!recorder) return;
  holdActive.value = true;

  isCancelling.value = false;
  await recorder.open();
  recorder.startRecord();

  if ("vibrate" in navigator) {
    navigator.vibrate(50);
  }
  // 30秒自动结束录音
  if (timer) {
    clearTimeout(timer);
  }
  timer = setTimeout(() => {
    endHoldAction();
  }, 30000);
};

const endHoldAction = async () => {
  if (!recorder) return;
  if (timer) {
    clearTimeout(timer);
  }
  if (holdActive.value) {
    holdActive.value = false;

    if (isCancelling.value) {
      // 取消录音
    } else {
      // 发送录音
      const audioBlob = await recorder.stopRecord();
      if (audioBlob) {
        emits("end", audioBlob);
      }
    }

    isCancelling.value = false;

    if ("vibrate" in navigator) {
      navigator.vibrate(25);
    }
  }
  recorder.close();
};
</script>
